import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControlLabel,
  Switch,
  Alert,
  Typography,
  Box,
  InputAdornment,
  Autocomplete,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  Stack,
  Chip,
} from '@mui/material';
import {
  Person as PersonIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { coachingSessionAPI, memberProfileAPI } from '../../services/api';
import { CoachingSession, BookCoachingSessionData } from '../../types/coaching';
import { MemberProfile, UserRole } from '../../types';
import { parseDateOnlyString, formatTimeString } from '../../utils/dateUtils';
import { useAuth } from '../../context/AuthContext';

interface MemberEntry {
  id: number;
  isGuest: boolean;
  selectedMember: MemberProfile | null;
  guestName: string;
  guestEmail: string;
  startTime: Date;
  endTime: Date;
  memberNotes: string;
  overrideCost: boolean;
  overriddenCost: number;
  error?: string;
}

interface BookingResult {
  name: string;
  isGuest: boolean;
  success: boolean;
  error?: string;
}

interface BookCoachingSessionDialogProps {
  open: boolean;
  onClose: () => void;
  session: CoachingSession | null;
  onSuccess?: (message?: string) => void;
}

const BookCoachingSessionDialog: React.FC<BookCoachingSessionDialogProps> = ({
  open,
  onClose,
  session,
  onSuccess,
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [memberProfiles, setMemberProfiles] = useState<MemberProfile[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [memberEntries, setMemberEntries] = useState<MemberEntry[]>([]);
  const [nextId, setNextId] = useState(0);
  const [results, setResults] = useState<BookingResult[] | null>(null);
  const [globalError, setGlobalError] = useState<string | null>(null);

  useEffect(() => {
    if (open && session) {
      setMemberEntries([]);
      setNextId(0);
      setResults(null);
      setGlobalError(null);
      fetchMembers();
    }
  }, [open, session]);

  const fetchMembers = async () => {
    try {
      setLoadingMembers(true);
      const response = await memberProfileAPI.getAllMemberProfiles();
      const activeMembers = response.data.filter((m: MemberProfile) => m.status === 'active');
      setMemberProfiles(activeMembers);
    } catch (err: any) {
      console.error('Error fetching members:', err);
      setGlobalError('Failed to fetch member profiles');
    } finally {
      setLoadingMembers(false);
    }
  };

  const calculateSessionCost = (sess: CoachingSession, durationMinutes: number, isGuest: boolean): number => {
    if (isGuest && sess.guestRateCost && sess.guestRateDuration)
      return (durationMinutes / sess.guestRateDuration) * sess.guestRateCost;
    if (!isGuest && sess.memberRateCost && sess.memberRateDuration)
      return (durationMinutes / sess.memberRateDuration) * sess.memberRateCost;
    return 0;
  };

  const makeDefaultEntry = (id: number): MemberEntry => {
    const parts = session!.startTime.split(':');
    const start = new Date();
    start.setHours(parseInt(parts[0], 10), parseInt(parts[1], 10), 0, 0);
    const end = new Date(start.getTime() + session!.minBookingDurationMinutes * 60000);
    const cost = calculateSessionCost(session!, session!.minBookingDurationMinutes, false);
    return {
      id, isGuest: false, selectedMember: null,
      guestName: '', guestEmail: '',
      startTime: start, endTime: end,
      memberNotes: '', overrideCost: false, overriddenCost: cost,
    };
  };

  const availableSlots = session ? (session.availableSlots ?? (session.capacity - (session.currentBookings ?? 0))) : 0;

  const addEntry = () => {
    if (!session) return;
    setMemberEntries(prev => [...prev, makeDefaultEntry(nextId)]);
    setNextId(prev => prev + 1);
  };

  const removeEntry = (id: number) => {
    setMemberEntries(prev => prev.filter(e => e.id !== id));
  };

  const updateEntry = (id: number, updates: Partial<MemberEntry>) => {
    setMemberEntries(prev => prev.map(e => (e.id === id ? { ...e, ...updates } : e)));
  };

  const calculateEntryDuration = (entry: MemberEntry) =>
    Math.floor((entry.endTime.getTime() - entry.startTime.getTime()) / 60000);

  const canOverrideCost =
    user &&
    [UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.COACH].includes(user.role);

  const validateAndSubmit = async () => {
    if (!session) return;
    setGlobalError(null);

    if (memberEntries.length === 0) {
      setGlobalError('Please add at least one member before booking.');
      return;
    }

    // Check capacity
    const availableNow = session.availableSlots ?? (session.capacity - (session.currentBookings ?? 0));
    if (memberEntries.length > availableNow) {
      setGlobalError(`Only ${availableNow} slot${availableNow !== 1 ? 's' : ''} available. Remove ${memberEntries.length - availableNow} member${memberEntries.length - availableNow !== 1 ? 's' : ''} before booking.`);
      return;
    }

    // Check for duplicate members
    const selectedMemberIds = memberEntries
      .filter(e => !e.isGuest && e.selectedMember)
      .map(e => e.selectedMember!.id);
    const duplicateIds = selectedMemberIds.filter((id, idx) => selectedMemberIds.indexOf(id) !== idx);
    if (duplicateIds.length > 0) {
      setGlobalError('The same member cannot be booked more than once for this session.');
      return;
    }

    let hasError = false;
    const validated = memberEntries.map(entry => {
      if (entry.startTime >= entry.endTime)
        return { ...entry, error: 'End time must be after start time' };
      const duration = calculateEntryDuration(entry);
      if (duration < session.minBookingDurationMinutes)
        return { ...entry, error: `Minimum booking duration is ${session.minBookingDurationMinutes} minutes` };
      if (!entry.isGuest && !entry.selectedMember)
        return { ...entry, error: 'Please select a member' };
      if (entry.isGuest && !entry.guestName.trim())
        return { ...entry, error: 'Guest name is required' };
      if (entry.isGuest && !entry.guestEmail.trim())
        return { ...entry, error: 'Guest email is required' };
      return { ...entry, error: undefined };
    });

    hasError = validated.some(e => !!e.error);
    setMemberEntries(validated);
    if (hasError) return;

    setLoading(true);
    const bookingResults: BookingResult[] = [];

    for (const entry of validated) {
      const duration = calculateEntryDuration(entry);
      const originalCalculatedCost = calculateSessionCost(session, duration, entry.isGuest);
      const name = entry.isGuest
        ? entry.guestName
        : `${entry.selectedMember!.firstName} ${entry.selectedMember!.lastName}`;

      try {
        const bookingData: BookCoachingSessionData = {
          startTime: entry.startTime.toTimeString().split(' ')[0],
          endTime: entry.endTime.toTimeString().split(' ')[0],
          durationMinutes: duration,
          memberNotes: entry.memberNotes,
          memberProfileId: entry.isGuest ? undefined : entry.selectedMember?.id,
          isGuest: entry.isGuest,
          guestName: entry.isGuest ? entry.guestName : undefined,
          guestEmail: entry.isGuest ? entry.guestEmail : undefined,
          overrideCost: entry.overrideCost,
          overriddenCost: entry.overrideCost ? entry.overriddenCost : undefined,
          originalCalculatedCost: entry.overrideCost ? originalCalculatedCost : undefined,
        };
        await coachingSessionAPI.bookCoachingSession(session.id, bookingData);
        bookingResults.push({ name, isGuest: entry.isGuest, success: true });
      } catch (err: any) {
        const errorMsg =
          err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          'Booking failed';
        bookingResults.push({ name, isGuest: entry.isGuest, success: false, error: errorMsg });
      }
    }

    setLoading(false);
    setResults(bookingResults);

    const succeeded = bookingResults.filter(r => r.success).length;
    if (succeeded > 0) onSuccess?.();
  };

  const handleDone = () => {
    const succeeded = results?.filter(r => r.success).length ?? 0;
    const total = results?.length ?? 0;
    const message =
      succeeded > 0
        ? `${succeeded} of ${total} booking${total > 1 ? 's' : ''} added successfully`
        : undefined;
    if (message) onSuccess?.(message);
    onClose();
  };

  if (!session) return null;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog open={open} disableEscapeKeyDown maxWidth="md" fullWidth>
        <DialogTitle>Book Coaching Session — {session.name}</DialogTitle>

        <DialogContent>
          {/* Session info banner */}
          <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2, mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Coach:</strong> {session.coachName} •{' '}
              <strong>Venues:</strong>{' '}
              {session.venues?.length > 0 ? `${session.venues.length} venue(s)` : 'Not specified'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Date:</strong> {parseDateOnlyString(session.sessionDate).toDateString()} •{' '}
              <strong>Available:</strong> {formatTimeString(session.startTime)} –{' '}
              {formatTimeString(session.endTime)} •{' '}
              <strong>Slots remaining:</strong>{' '}
              {availableSlots} / {session.capacity}
            </Typography>
          </Box>

          {/* ── Results view ── */}
          {results ? (
            <Stack spacing={1}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Booking Results
              </Typography>
              {results.map((r, i) => (
                <Box
                  key={i}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 1.5,
                    borderRadius: 1,
                    bgcolor: r.success ? 'rgba(56,142,60,0.08)' : 'rgba(211,47,47,0.08)',
                    border: '1px solid',
                    borderColor: r.success ? 'success.light' : 'error.light',
                  }}
                >
                  {r.success ? (
                    <CheckCircleIcon color="success" fontSize="small" />
                  ) : (
                    <ErrorIcon color="error" fontSize="small" />
                  )}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight="medium">{r.name}</Typography>
                    {r.error && (
                      <Typography variant="caption" color="error">{r.error}</Typography>
                    )}
                  </Box>
                  {r.isGuest && <Chip label="Guest" size="small" variant="outlined" />}
                  <Chip
                    label={r.success ? 'Booked' : 'Failed'}
                    size="small"
                    color={r.success ? 'success' : 'error'}
                  />
                </Box>
              ))}
            </Stack>
          ) : (
            /* ── Input view ── */
            <>
              {globalError && (
                <Alert severity="error" sx={{ mb: 2 }}>{globalError}</Alert>
              )}

              <Stack spacing={2}>
                {memberEntries.map((entry, index) => (
                  <Card key={entry.id} variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Member {index + 1}
                        </Typography>
                        <IconButton size="small" color="error" onClick={() => removeEntry(entry.id)} title="Remove">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>

                      {entry.error && (
                        <Alert severity="error" sx={{ mb: 1.5 }}>{entry.error}</Alert>
                      )}

                      <Grid container spacing={2}>
                        {/* Guest toggle */}
                        {session.allowGuestBookings && (
                          <Grid size={{ xs: 12 }}>
                            <FormControlLabel
                              control={
                                <Switch
                                  size="small"
                                  checked={entry.isGuest}
                                  onChange={(e) =>
                                    updateEntry(entry.id, {
                                      isGuest: e.target.checked,
                                      selectedMember: e.target.checked ? null : entry.selectedMember,
                                    })
                                  }
                                />
                              }
                              label="Guest"
                            />
                          </Grid>
                        )}

                        {/* Member selector */}
                        {!entry.isGuest ? (
                          <Grid size={{ xs: 12 }}>
                            <Autocomplete
                              options={memberProfiles}
                              value={entry.selectedMember}
                              onChange={(_, val) => updateEntry(entry.id, { selectedMember: val })}
                              getOptionLabel={(o) => `${o.firstName} ${o.lastName} (${o.email})`}
                              getOptionKey={(o) => o.id}
                              getOptionDisabled={(o) =>
                                memberEntries.some(e => e.id !== entry.id && !e.isGuest && e.selectedMember?.id === o.id)
                              }
                              loading={loadingMembers}
                              renderOption={(props, option) => (
                                <Box component="li" {...props}>
                                  <PersonIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }} />
                                  <Box>
                                    <Typography variant="body2">{option.firstName} {option.lastName}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {option.email} • {option.membershipTier?.name || 'No Tier'}
                                    </Typography>
                                  </Box>
                                </Box>
                              )}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Select Member"
                                  size="small"
                                  required
                                  InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                      <>
                                        {loadingMembers && <CircularProgress color="inherit" size={16} />}
                                        {params.InputProps.endAdornment}
                                      </>
                                    ),
                                  }}
                                />
                              )}
                              noOptionsText={loadingMembers ? 'Loading members...' : 'No members found'}
                            />
                          </Grid>
                        ) : (
                          /* Guest fields */
                          <>
                            <Grid size={{ xs: 12, md: 6 }}>
                              <TextField
                                label="Guest Name" size="small" fullWidth required
                                value={entry.guestName}
                                onChange={(e) => updateEntry(entry.id, { guestName: e.target.value })}
                              />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                              <TextField
                                label="Guest Email" type="email" size="small" fullWidth required
                                value={entry.guestEmail}
                                onChange={(e) => updateEntry(entry.id, { guestEmail: e.target.value })}
                              />
                            </Grid>
                          </>
                        )}

                        {/* Start / End Time */}
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TimePicker
                            label="Start Time"
                            value={entry.startTime}
                            onChange={(t) => {
                              if (!t) return;
                              const end = new Date(t.getTime() + session.minBookingDurationMinutes * 60000);
                              const duration = Math.floor((end.getTime() - t.getTime()) / 60000);
                              const cost = calculateSessionCost(session, duration, entry.isGuest);
                              updateEntry(entry.id, {
                                startTime: t, endTime: end,
                                overriddenCost: entry.overrideCost ? entry.overriddenCost : cost,
                              });
                            }}
                            slotProps={{ textField: { fullWidth: true, size: 'small', required: true } }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TimePicker
                            label="End Time"
                            value={entry.endTime}
                            onChange={(t) => t && updateEntry(entry.id, { endTime: t })}
                            slotProps={{ textField: { fullWidth: true, size: 'small', required: true } }}
                          />
                        </Grid>

                        {/* Duration / Cost summary */}
                        <Grid size={{ xs: 12 }}>
                          <Box sx={{ p: 1.5, bgcolor: 'info.50', borderRadius: 1 }}>
                            <Typography variant="body2">
                              <strong>Duration:</strong> {calculateEntryDuration(entry)} min
                              {(session.memberRateCost || session.guestRateCost) && (
                                <> &nbsp;•&nbsp; <strong>{entry.overrideCost ? 'Original Cost:' : 'Cost:'}</strong> ${calculateSessionCost(session, calculateEntryDuration(entry), entry.isGuest).toFixed(2)}</>
                              )}
                            </Typography>
                          </Box>
                        </Grid>

                        {/* Cost Override */}
                        {canOverrideCost && (session.memberRateCost || session.guestRateCost) && (
                          <>
                            <Grid size={{ xs: 12 }}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    size="small"
                                    checked={entry.overrideCost}
                                    onChange={(e) => {
                                      const isOverriding = e.target.checked;
                                      const cost = calculateSessionCost(session, calculateEntryDuration(entry), entry.isGuest);
                                      updateEntry(entry.id, {
                                        overrideCost: isOverriding,
                                        overriddenCost: isOverriding ? entry.overriddenCost : cost,
                                      });
                                    }}
                                  />
                                }
                                label="Override Cost"
                              />
                            </Grid>
                            {entry.overrideCost && (
                              <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                  label="New Cost" type="number" size="small" fullWidth required
                                  value={entry.overriddenCost}
                                  onChange={(e) => {
                                    const v = parseFloat(e.target.value);
                                    updateEntry(entry.id, { overriddenCost: isNaN(v) ? 0 : v });
                                  }}
                                  inputProps={{ min: 0, step: 0.01 }}
                                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                  helperText="Enter the overridden cost for this member."
                                />
                              </Grid>
                            )}
                          </>
                        )}

                        {/* Notes */}
                        <Grid size={{ xs: 12 }}>
                          <TextField
                            label="Notes" size="small" fullWidth multiline rows={2}
                            value={entry.memberNotes}
                            onChange={(e) => updateEntry(entry.id, { memberNotes: e.target.value })}
                            placeholder="Any special requests or notes for the coach"
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                ))}
              </Stack>

              {/* Add Member button */}
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addEntry}
                  disabled={loadingMembers || memberEntries.length >= availableSlots}
                  title={memberEntries.length >= availableSlots ? `Session is at capacity (${session.capacity})` : undefined}
                >
                  Add Member
                </Button>
                {memberEntries.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Click "Add Member" to start enrolling participants.
                  </Typography>
                )}
              </Box>
            </>
          )}
        </DialogContent>

        <DialogActions>
          {results ? (
            <Button onClick={handleDone} variant="contained">Done</Button>
          ) : (
            <>
              <Button onClick={onClose} disabled={loading}>Cancel</Button>
              <Button
                onClick={validateAndSubmit}
                variant="contained"
                disabled={loading || memberEntries.length === 0}
              >
                {loading ? (
                  <><CircularProgress size={20} sx={{ mr: 1 }} />Booking...</>
                ) : (
                  `Book Session${memberEntries.length > 0 ? ` (${memberEntries.length})` : ''}`
                )}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default BookCoachingSessionDialog;

